'use client';

import { useTableFilter } from '@/hooks/use-table-filter';
import { cn } from '@/lib/cn';
import { ObjectKeys } from '@/lib/utils';
import { useSession } from '@/providers/session-provider';
import { Icons } from '@/components/icons';
import { useState } from 'react';
import { useReadGlobalFilter } from '../../lib/global-filter/hooks/use-global-filter';
import { Button } from '../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  type AppliedFilter,
  ENUM_FILTER,
  FILTER_KINDS,
  type Filter,
  type FilterValue,
  NULL_FILTER,
  RELATION_FILTER,
} from './constants';
import { CurrentCreatingFilter } from './current-creating';
import { FilterItem } from './filter-item';

interface TableFilterProps {
  clientState?: boolean;
  filters: Filter[];
  tableId: string;
}

export default function TableFilters({
  clientState,
  filters,
  tableId,
}: TableFilterProps) {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [currentCreating, setCurrentCreating] = useState<Filter | null>(null);
  const {
    filter,
    deleteAppliedFilterValue,
    setTableFilter,
    setFilterValue,
    updateAppliedFilterOperator,
    lastUsedFilter,
    getAllAppliedFilters,
  } = useTableFilter({
    isClient: clientState,
    tableId,
  });

  const {
    data: {
      session: { organizationId },
    },
  } = useSession();
  const globalFilter = useReadGlobalFilter();

  const hasAnyGlobalFilter = ObjectKeys(globalFilter).some(
    (gf) => globalFilter[gf].length > 0
  );

  const createFilter = (val: FilterValue, explicitFilter?: Filter) => {
    const filterToCreate = currentCreating || explicitFilter;
    if (!filterToCreate) {
      return;
    }
    // If the user does not choose any relations, we default to "Har ett värde" aka __isnull!=true.
    // Filters that don't support __isnull! (e.g. noop-only global segments) can't render that
    // fallback as a chip, so an empty selection cancels the creation instead.
    if (
      filterToCreate.kind === RELATION_FILTER &&
      Array.isArray(val) &&
      val.length === 0
    ) {
      if (filterToCreate.operators.includes('__isnull!')) {
        setFilterValue(filterToCreate, true, '__isnull!');
      }
    } else {
      setFilterValue(filterToCreate, val);
    }

    setCurrentCreating(null);
  };

  const getFilterConfig = (filter: AppliedFilter) => {
    return filters.find(
      (f) =>
        f.externalFieldName === filter.externalFieldName &&
        f.operators.includes(filter.operator)
    );
  };

  const appliedFilters = getAllAppliedFilters().filter(
    (appliedFilter) =>
      getFilterConfig(appliedFilter)?.kind !== FILTER_KINDS.ENUM_FILTER
  );

  const isIconOnly = !!currentCreating || appliedFilters.length > 0;

  return (
    <>
      {appliedFilters.map((appliedFilter) => {
        const filterConfig = filters.find(
          (f) =>
            f.externalFieldName === appliedFilter.externalFieldName &&
            f.operators.includes(appliedFilter.operator)
        );

        if (!filterConfig) {
          return null;
        }
        if (appliedFilter.value === undefined) {
          return null;
        }

        return (
          <FilterItem
            TableFilters={TableFilters}
            key={`${appliedFilter.externalFieldName}${appliedFilter.operator}`}
            filterConfig={filterConfig}
            filter={appliedFilter}
            updateFilterOperator={(operator) =>
              updateAppliedFilterOperator(
                appliedFilter,
                operator,
                filterConfig.kind
              )
            }
            updateFilterValue={(newVal) =>
              setFilterValue(filterConfig, newVal, appliedFilter.operator)
            }
            removeFilter={() => {
              deleteAppliedFilterValue(appliedFilter);
            }}
          />
        );
      })}

      {currentCreating && (
        <CurrentCreatingFilter
          TableFilters={TableFilters}
          currentCreating={currentCreating}
          onDone={(val) => {
            if (val == null) {
              setCurrentCreating(null);
            } else {
              createFilter(val);
            }
          }}
        />
      )}
      <Popover
        open={selectorOpen}
        onOpenChange={(open) => setSelectorOpen(open)}
        modal
      >
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            aria-label={isIconOnly ? 'Lägg till filter' : undefined}
            className={cn('rounded-full text-xs', {
              'p-1.5': isIconOnly,
            })}
            size={isIconOnly ? 'icon-sm' : 'default'}
          >
            {isIconOnly ? (
              <Icons.add className='size-4' />
            ) : (
              <>
                Lägg till filter
                <Icons.addCircle className='ml-2 size-4' />
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='p-0'>
          <Command>
            <CommandInput placeholder='Sök bland filter...' />
            <CommandList>
              <CommandEmpty>Hittade inga filter.</CommandEmpty>
              <CommandGroup>
                {filters
                  .filter((f) => f.kind !== ENUM_FILTER)
                  .map((filter) => (
                    <CommandItem
                      onSelect={() => {
                        setSelectorOpen(false);

                        if (filter.kind === NULL_FILTER) {
                          createFilter(true, filter);
                        } else {
                          setCurrentCreating(filter);
                        }
                      }}
                      key={filter.id ?? filter.externalFieldName + filter.kind}
                      value={filter.title}
                    >
                      {filter.title}
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {ObjectKeys(filter ?? {}).length === 0 &&
        organizationId &&
        !hasAnyGlobalFilter &&
        ObjectKeys(lastUsedFilter?.[organizationId] ?? {}).length > 0 && (
          <Button
            onClick={() => {
              setTableFilter(lastUsedFilter?.[organizationId]);
            }}
            className='rounded-full text-xs'
            variant='tertiary'
          >
            Använd senaste filtrering <Icons.add className='ml-2 size-4' />
          </Button>
        )}
    </>
  );
}
